import { create } from 'zustand';
import { useAuth } from '@/hooks/useAuth';
import { useCurrencyStore } from '@/stores/currency-store';

type PendingReveal = {
	betId: string;
	game: string; // e.g., 'coinflip', 'limbo', 'dice', 'plinko', 'lootbox', 'suiroyale'
	createdAt: number;
};

interface InternalState {
	reveals: PendingReveal[];
	addReveal: (betId: string, game: string) => void;
	removeReveal: (betId: string) => void;
	listForGame: (game: string) => string[];
}

const usePendingRevealStore = create<InternalState>((set, get) => ({
	reveals: [],
	addReveal: (betId, game) =>
		set((state) => ({ reveals: [...state.reveals.filter((r) => r.betId !== betId), { betId, game, createdAt: Date.now() }] })),
	removeReveal: (betId) => set((state) => ({ reveals: state.reveals.filter((r) => r.betId !== betId) })),
	listForGame: (game) =>
		get()
			.reveals.filter((r) => r.game === game)
			.map((r) => r.betId),
}));

type StartArgs = {
	game: string;
	stakeBaseUnits: number;
	betId?: string; // optional until you receive it from chain
	currencyType?: string;
};

type SetPayoutArgs = {
	balanceBetId: string;
	payoutBaseUnits: number;
};

type FinalizeArgs = {
	balanceBetId: string | null;
	betId?: string;
};

type RegisterArgs = { betId: string; game: string };

export function usePendingReveal() {
	const { addPendingBet, setPendingBetPayout, setPendingBetGas, resolvePendingBet } = useAuth();
	const selectedCurrency = useCurrencyStore((s) => s.selectedCurrency);
	const addReveal = usePendingRevealStore((s) => s.addReveal);
	const removeReveal = usePendingRevealStore((s) => s.removeReveal);
	const listForGame = usePendingRevealStore((s) => s.listForGame);

	function start({ game, stakeBaseUnits, betId, currencyType }: StartArgs) {
		const balanceBetId = addPendingBet(stakeBaseUnits, undefined, currencyType || selectedCurrency.type);

		if (betId) {
			addReveal(betId, game);
		}
		return { balanceBetId };
	}

	function setPayout({ balanceBetId, payoutBaseUnits }: SetPayoutArgs) {
		setPendingBetPayout(balanceBetId, payoutBaseUnits);
	}

	function setGas({ balanceBetId, gasBaseUnits }: { balanceBetId: string; gasBaseUnits: number }) {
		setPendingBetGas(balanceBetId, gasBaseUnits);
	}

	function finalize({ balanceBetId, betId }: FinalizeArgs) {
		if (balanceBetId) resolvePendingBet(balanceBetId);
		if (betId) removeReveal(betId);
	}

	function registerBetId({ betId, game }: RegisterArgs) {
		addReveal(betId, game);
	}

	return {
		start,
		setPayout,
		setGas,
		finalize,
		registerBetId,
		pendingIdsFor: (game: string) => listForGame(game),
	};
}
